import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  MessageSquare,
  Bell,
  BellOff,
  Clock,
  AlertTriangle,
  Calendar,
  Save,
  Globe
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Common US timezones
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "UTC", label: "UTC (Coordinated Universal Time)" }
];

const NOTIFICATION_SOUNDS = [
  { value: "default", label: "Default Alert", description: "Standard notification sound" },
  { value: "urgent", label: "Urgent", description: "Attention-grabbing alarm" },
  { value: "gentle", label: "Gentle", description: "Soft, non-intrusive tone" },
  { value: "silent", label: "Silent", description: "Visual only, no sound" }
];

const SMSPreferences = ({ user }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    receive_crisis_alerts: true,
    receive_quick_help_alerts: true,
    receive_appointment_reminders: true,
    quiet_hours_start: "",
    quiet_hours_end: "",
    timezone: "",
    notification_sound: "default"
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  // Auto-detect browser timezone
  const detectTimezone = () => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Check if it's in our supported list
      const supported = TIMEZONES.find(tz => tz.value === browserTimezone);
      if (supported) {
        return browserTimezone;
      }
      // Try to map common browser timezones to our list
      if (browserTimezone.includes("America/New_York") || browserTimezone.includes("US/Eastern")) {
        return "America/New_York";
      }
      if (browserTimezone.includes("America/Chicago") || browserTimezone.includes("US/Central")) {
        return "America/Chicago";
      }
      if (browserTimezone.includes("America/Denver") || browserTimezone.includes("US/Mountain")) {
        return "America/Denver";
      }
      if (browserTimezone.includes("America/Los_Angeles") || browserTimezone.includes("US/Pacific")) {
        return "America/Los_Angeles";
      }
      return null;
    } catch {
      return null;
    }
  };

  const loadPreferences = async () => {
    try {
      const response = await axios.get(`${API}/responder/sms-preferences`);
      const savedTimezone = response.data.timezone;
      
      // Auto-detect if no timezone saved
      let timezone = savedTimezone || "";
      if (!savedTimezone) {
        const detected = detectTimezone();
        if (detected) {
          timezone = detected;
        }
      }
      
      setPrefs({
        receive_crisis_alerts: response.data.receive_crisis_alerts ?? true,
        receive_quick_help_alerts: response.data.receive_quick_help_alerts ?? true,
        receive_appointment_reminders: response.data.receive_appointment_reminders ?? true,
        quiet_hours_start: response.data.quiet_hours_start || "",
        quiet_hours_end: response.data.quiet_hours_end || "",
        timezone: timezone,
        notification_sound: response.data.notification_sound || "default"
      });
    } catch (error) {
      console.error("Failed to load preferences:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/responder/sms-preferences`, prefs);
      toast.success("SMS preferences saved!");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAutoDetectTimezone = () => {
    const detected = detectTimezone();
    if (detected) {
      setPrefs({...prefs, timezone: detected});
      toast.success("Timezone detected!");
    } else {
      toast.error("Could not detect your timezone. Please select manually.");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/profile")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              SMS Preferences
            </span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Alert Types */}
        <Card className="card-valor mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Bell className="w-5 h-5 text-primary" />
              Alert Types
            </CardTitle>
            <CardDescription>
              Choose which types of SMS alerts you want to receive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Crisis Alerts */}
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <Label className="font-semibold">Crisis Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Urgent notifications when veterans request in-person or phone support
                  </p>
                </div>
              </div>
              <Switch
                checked={prefs.receive_crisis_alerts}
                onCheckedChange={(checked) => setPrefs({...prefs, receive_crisis_alerts: checked})}
                data-testid="crisis-alerts-toggle"
              />
            </div>

            {/* Quick Help Alerts */}
            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-100">
              <div className="flex items-start gap-3">
                <MessageSquare className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <Label className="font-semibold">Quick Help Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Notifications for quick help requests from veterans without accounts
                  </p>
                </div>
              </div>
              <Switch
                checked={prefs.receive_quick_help_alerts}
                onCheckedChange={(checked) => setPrefs({...prefs, receive_quick_help_alerts: checked})}
                data-testid="quick-help-alerts-toggle"
              />
            </div>

            {/* Appointment Reminders */}
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <Label className="font-semibold">Appointment Reminders</Label>
                  <p className="text-sm text-muted-foreground">
                    Reminders for upcoming scheduled appointments
                  </p>
                </div>
              </div>
              <Switch
                checked={prefs.receive_appointment_reminders}
                onCheckedChange={(checked) => setPrefs({...prefs, receive_appointment_reminders: checked})}
                data-testid="appointment-alerts-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Quiet Hours */}
        <Card className="card-valor mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <BellOff className="w-5 h-5 text-primary" />
              Quiet Hours
            </CardTitle>
            <CardDescription>
              Set times when you don't want to receive SMS alerts (optional)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quiet-start" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Start Time
                </Label>
                <Input
                  id="quiet-start"
                  type="time"
                  value={prefs.quiet_hours_start}
                  onChange={(e) => setPrefs({...prefs, quiet_hours_start: e.target.value})}
                  data-testid="quiet-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quiet-end" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  End Time
                </Label>
                <Input
                  id="quiet-end"
                  type="time"
                  value={prefs.quiet_hours_end}
                  onChange={(e) => setPrefs({...prefs, quiet_hours_end: e.target.value})}
                  data-testid="quiet-end-input"
                />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Example: Set 10:00 PM to 8:00 AM to avoid late night alerts
            </p>
            
            {/* Timezone Selection */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="space-y-2">
                <Label htmlFor="timezone" className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Your Timezone
                </Label>
                <Select
                  value={prefs.timezone || ""}
                  onValueChange={(value) => setPrefs({...prefs, timezone: value})}
                >
                  <SelectTrigger data-testid="timezone-select">
                    <SelectValue placeholder="Select your timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleAutoDetectTimezone}
                    data-testid="auto-detect-timezone-btn"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Detect My Timezone
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    or select manually above
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification Sound */}
        <Card className="card-valor mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Bell className="w-5 h-5 text-primary" />
              Notification Sound
            </CardTitle>
            <CardDescription>
              Choose alert sound for push notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-3">
              {NOTIFICATION_SOUNDS.map((sound) => (
                <div
                  key={sound.value}
                  onClick={() => setPrefs({...prefs, notification_sound: sound.value})}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    prefs.notification_sound === sound.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid={`sound-${sound.value}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{sound.label}</p>
                      <p className="text-sm text-muted-foreground">{sound.description}</p>
                    </div>
                    {prefs.notification_sound === sound.value && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Important Note */}
        <Card className="card-valor mb-6 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">Important</p>
                <p className="text-sm text-amber-700">
                  Disabling crisis alerts means you won't be notified when veterans need immediate help. 
                  Please only disable if another responder is covering your area.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full h-12"
          data-testid="save-prefs-btn"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Preferences
            </>
          )}
        </Button>
      </main>
    </div>
  );
};

export default SMSPreferences;
